import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

const SITE_URL = 'https://jornada.reacendaluz.com.br'
const GOLD = '#C8923A'
const GOLD_SOFT = '#E0B870'

interface Acesso {
  _id: string
  name: string
  email: string
  transaction: string
  status: string
  lastEvent?: string
  emailSentAt?: string
  createdAt: string
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function randomCode(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
    } catch {
      /* noop */
    }
    document.body.removeChild(ta)
  }
}

export default function AdminMapaCuraAcessos() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ link: string; name: string; email: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const [acessos, setAcessos] = useState<Acesso[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    void loadAcessos()
  }, [])

  async function loadAcessos() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.mapa_cura_acessos.list({
        sort: { createdAt: -1 },
        limit: 200,
        projection: {
          name: 1,
          email: 1,
          transaction: 1,
          status: 1,
          lastEvent: 1,
          emailSentAt: 1,
          createdAt: 1,
        },
      })
      setAcessos(list as Acesso[])
    } catch (e) {
      console.error('Erro ao carregar acessos:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate() {
    setError('')
    setResult(null)
    const trimName = name.trim()
    const trimEmail = email.trim().toLowerCase()

    if (trimName.length < 3) {
      setError('Informe o nome completo da pessoa.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setError('Informe um e-mail válido.')
      return
    }

    setCreating(true)
    try {
      const code = randomCode()
      const codeHash = await sha256Hex(code)
      const now = new Date().toISOString()

      await lumi.entities.mapa_cura_acessos.create({
        name: trimName,
        email: trimEmail,
        transaction: `manual-${now}-${code.slice(0, 8)}`,
        codeHash,
        status: 'active',
        productName: 'Mapa de Cura dos 4 Elementos',
        lastEvent: 'MANUAL_GRANT',
        observacoes: note.trim(),
        createdAt: now,
        updatedAt: now,
      })

      setResult({ link: `${SITE_URL}/mapa?acesso=${code}`, name: trimName, email: trimEmail })
      setName('')
      setEmail('')
      setNote('')
      await loadAcessos()
    } catch (e) {
      console.error('Erro ao criar acesso:', e)
      setError('Não foi possível criar o acesso agora. Tente novamente.')
    } finally {
      setCreating(false)
    }
  }

  async function toggleStatus(acesso: Acesso) {
    const next = acesso.status === 'active' ? 'revoked' : 'active'
    setBusyId(acesso._id)
    setAcessos(prev => prev.map(a => (a._id === acesso._id ? { ...a, status: next } : a)))
    try {
      await lumi.entities.mapa_cura_acessos.update(acesso._id, {
        status: next,
        updatedAt: new Date().toISOString(),
      })
    } catch (e) {
      console.error('Erro ao alterar status:', e)
      await loadAcessos()
    } finally {
      setBusyId(null)
    }
  }

  async function handleCopyLink() {
    if (!result) return
    await copyText(result.link)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* ── Novo acesso ── */}
      <div
        className="rounded-2xl p-5 sm:p-6"
        style={{ background: 'rgba(200,146,58,0.08)', border: '1px solid rgba(200,146,58,0.3)' }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-2" style={{ color: GOLD_SOFT }}>
          Acesso manual
        </p>
        <h2 className="font-display italic text-[20px] sm:text-[22px] text-[var(--ink)] mb-2">
          Liberar o Mapa de Cura sem passar pelo Hotmart
        </h2>
        <p className="text-[13px] text-[var(--ink-soft)] font-body leading-relaxed mb-5">
          Preencha o nome e o e-mail da pessoa. O sistema cria um código exclusivo e gera o link de acesso para você
          enviar por WhatsApp ou e-mail. Cada link abre só o mapa de quem o recebeu.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1.5">
              Nome completo
            </label>
            <input
              value={name}
              onChange={e => {
                setName(e.target.value)
                setError('')
              }}
              placeholder="Ex.: Maria Silva"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-[14px] text-[var(--ink)] placeholder-[var(--ink-soft)] font-body focus:outline-none focus:border-white/30 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1.5">
              E-mail
            </label>
            <input
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                setError('')
              }}
              placeholder="email@exemplo.com"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-[14px] text-[var(--ink)] placeholder-[var(--ink-soft)] font-body focus:outline-none focus:border-white/30 transition-colors"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1.5">
            Observação (opcional)
          </label>
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Ex.: pagou por Pix, cortesia, cliente antiga..."
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-[14px] text-[var(--ink)] placeholder-[var(--ink-soft)] font-body focus:outline-none focus:border-white/30 transition-colors"
          />
        </div>

        {error && (
          <p className="text-red-400 text-[12px] mt-3 font-body" role="alert">
            {error}
          </p>
        )}

        <button
          onClick={handleCreate}
          disabled={creating}
          className="mt-4 w-full sm:w-auto px-6 py-3 rounded-xl font-body text-[13px] tracking-[0.15em] uppercase transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
          style={{ background: 'rgba(200,146,58,0.22)', color: GOLD_SOFT, border: `1px solid ${GOLD}55` }}
        >
          {creating ? 'Gerando acesso...' : 'Gerar link de acesso'}
        </button>

        {result && (
          <div
            className="mt-5 rounded-xl p-4"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}
          >
            <p className="text-[11px] tracking-[0.2em] uppercase font-body mb-2" style={{ color: '#22c55e' }}>
              Acesso criado para {result.name}
            </p>
            <p className="text-[12px] text-[var(--ink)] font-body break-all mb-3">{result.link}</p>
            <p className="text-[12px] text-[var(--ink-soft)] font-body leading-relaxed mb-3">
              Copie e envie este link para {result.email}. Ele é pessoal e não deve ser compartilhado. Guarde-o: por
              segurança, ele não pode ser exibido de novo depois.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCopyLink}
                className="px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase transition-colors"
                style={{
                  color: copied ? '#22c55e' : GOLD_SOFT,
                  background: copied ? 'rgba(34,197,94,0.12)' : 'rgba(200,146,58,0.18)',
                  border: `1px solid ${copied ? 'rgba(34,197,94,0.35)' : 'rgba(200,146,58,0.35)'}`,
                }}
              >
                {copied ? 'Copiado' : 'Copiar link'}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Olá, ${result.name.split(' ')[0]}! Seu acesso ao Mapa de Cura dos 4 Elementos está aqui: ${result.link}`,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase no-underline transition-colors"
                style={{ color: GOLD_SOFT, background: 'rgba(200,146,58,0.12)', border: '1px solid rgba(200,146,58,0.3)' }}
              >
                Enviar por WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>

      {/* ── Lista de acessos ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[0.3em] uppercase font-body text-[var(--ink-soft)]">
            Acessos cadastrados
          </p>
          <button
            onClick={loadAcessos}
            className="text-[11px] font-body tracking-[0.1em] uppercase text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors"
          >
            Atualizar
          </button>
        </div>

        {loading && acessos.length === 0 ? (
          <p className="text-[13px] font-body text-[var(--ink-soft)] py-6">Carregando acessos...</p>
        ) : acessos.length === 0 ? (
          <p className="text-[13px] font-body text-[var(--ink-soft)] py-6">
            Nenhum acesso cadastrado ainda. O primeiro aparecerá aqui assim que você gerar um link.
          </p>
        ) : (
          <div className="space-y-2">
            {acessos.map(acesso => {
              const active = acesso.status === 'active'
              return (
                <div
                  key={acesso._id}
                  className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-body text-[15px] text-[var(--ink)] truncate">{acesso.name}</p>
                    <p className="font-body text-[12px] text-[var(--ink-soft)] truncate">{acesso.email}</p>
                    <p className="font-body text-[11px] text-[var(--ink-soft)] mt-1">
                      {acesso.lastEvent === 'MANUAL_GRANT' ? 'Manual' : 'Hotmart'} · criado em{' '}
                      {formatDate(acesso.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className="text-[10px] font-body tracking-[0.15em] uppercase px-2 py-1 rounded-lg"
                      style={{
                        color: active ? '#22c55e' : '#9ca3af',
                        background: active ? 'rgba(34,197,94,0.12)' : 'rgba(156,163,175,0.12)',
                        border: `1px solid ${active ? 'rgba(34,197,94,0.35)' : 'rgba(156,163,175,0.3)'}`,
                      }}
                    >
                      {active ? 'Ativo' : 'Revogado'}
                    </span>
                    <button
                      onClick={() => toggleStatus(acesso)}
                      disabled={busyId === acesso._id}
                      className="px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase transition-colors disabled:opacity-50"
                      style={{
                        color: active ? '#f87171' : GOLD_SOFT,
                        background: active ? 'rgba(239,68,68,0.1)' : 'rgba(200,146,58,0.14)',
                        border: `1px solid ${active ? 'rgba(239,68,68,0.3)' : 'rgba(200,146,58,0.3)'}`,
                      }}
                    >
                      {active ? 'Revogar' : 'Reativar'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
